import { useEffect, useState } from 'react';
import DictionaryTable from '../components/DictionaryTable';
import DictionaryForm from '../components/DictionaryForm';
import { dictionaryAPI, type DictionaryEntry } from '../services/api';

export default function Dictionary() {
  const [entries, setEntries] = useState<DictionaryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingEntry, setEditingEntry] = useState<DictionaryEntry | undefined>();
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    loadEntries();
  }, []);

  const loadEntries = async () => {
    try {
      setLoading(true);
      const data = await dictionaryAPI.getAll();
      setEntries(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingEntry(undefined);
    setShowForm(true);
  };

  const handleEdit = (entry: DictionaryEntry) => {
    setEditingEntry(entry);
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this entry?')) return;

    try {
      await dictionaryAPI.delete(id);
      await loadEntries();
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleSubmit = async (data: Partial<DictionaryEntry>) => {
    try {
      if (editingEntry?.id) {
        await dictionaryAPI.update(editingEntry.id, data);
      } else {
        await dictionaryAPI.create(data);
      }
      setShowForm(false);
      setEditingEntry(undefined);
      await loadEntries();
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingEntry(undefined);
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  if (error) {
    return (
      <div className="card">
        <div className="text-center py-8">
          <div className="text-red-600 dark:text-red-400 font-semibold mb-2">Error: {error}</div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-4">
            Make sure the backend server is running on port 8787.
          </p>
        </div>
      </div>
    );
  }

  if (showForm) {
    return (
      <DictionaryForm
        entry={editingEntry}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
      />
    );
  }

  return (
    <div>
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Data Dictionary</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">Manage all dictionary entries</p>
        </div>
        <button onClick={handleCreate} className="btn btn-primary">
          Create Entry
        </button>
      </div>

      <DictionaryTable
        data={entries}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
    </div>
  );
}

